type ConfigurableOption = {
  id?: number | string;
  label?: string;
};

type ConfigurableAttribute = {
  code?: string;
  options?: ConfigurableOption[];
};

type VirtualProduct = Record<string, unknown>;

const PACK_SIZE_LABELS = ['1m2', '1m²', '1 m2', '1 m²'];

function isUnsetOption(value: unknown): boolean {
  return value === null || value === undefined || Number.isNaN(Number(value));
}

export function isPackSizeLabel(label: unknown): boolean {
  return typeof label === 'string' && PACK_SIZE_LABELS.includes(label);
}

function cheapestVirtualProduct(virtualProducts: VirtualProduct[] | undefined): VirtualProduct | undefined {
  return (virtualProducts || []).reduce<VirtualProduct | undefined>((cheapest, product) => {
    const price = Number(product['final_price_incl_tax']);
    if (!Number.isFinite(price)) {
      return cheapest;
    }
    if (!cheapest) {
      return product;
    }
    return price < Number(cheapest['final_price_incl_tax']) ? product : cheapest;
  }, undefined);
}

function optionIdFromProduct(
  configurable: ConfigurableAttribute,
  product: VirtualProduct
): number | undefined {
  const code = configurable.code || 'sw_sizeruler';
  const raw = product[`${code}_option_id`] ?? product['sw_sizeruler_option_id'];
  const fromField = parseInt(String(raw ?? ''), 10);
  if (!Number.isNaN(fromField)) {
    return fromField;
  }

  const option = (configurable.options || []).find((candidate) => candidate.label === product[code]);
  const fromLabel = parseInt(String(option?.id ?? ''), 10);
  return Number.isNaN(fromLabel) ? undefined : fromLabel;
}

/**
 * Seed the cheapest child when Vue has no selection yet.
 * Magento option sort often puts "Pak" first, which is not the vanaf price.
 */
export function mergeSelectedOptions(
  configurableData: ConfigurableAttribute[] | undefined,
  selectedOptions: Array<number | null | undefined>,
  virtualProducts?: VirtualProduct[]
): number[] {
  const cheapest = cheapestVirtualProduct(virtualProducts);
  const merged: number[] = [];

  (configurableData || []).forEach((configurable, index) => {
    const current = selectedOptions[index];
    if (!isUnsetOption(current)) {
      merged[index] = Number(current);
      return;
    }

    const cheapestId = cheapest ? optionIdFromProduct(configurable, cheapest) : undefined;
    if (cheapestId !== undefined) {
      merged[index] = cheapestId;
      return;
    }

    const firstId = parseInt(String(configurable.options?.[0]?.id ?? ''), 10);
    if (!Number.isNaN(firstId)) {
      merged[index] = firstId;
    }
  });

  return merged;
}

export function isPackSizeOneSquareMeter(selectedProduct: Record<string, unknown> | null | undefined): boolean {
  return Boolean(selectedProduct && Object.keys(selectedProduct).length > 0 && isPackSizeLabel(selectedProduct['sw_sizeruler']));
}

export function selectedOptionIsPackSize(
  configurableData: ConfigurableAttribute[] | undefined,
  selectedOptions: Array<number | null | undefined>
): boolean {
  return (configurableData || []).some((configurable, index) => {
    const selectedId = selectedOptions[index];
    if (isUnsetOption(selectedId)) {
      return false;
    }

    const option = (configurable.options || []).find((candidate) => Number(candidate.id) === Number(selectedId));
    return isPackSizeLabel(option?.label);
  });
}

export function isConfigurableListItemSaleable(
  selectedProduct: Record<string, unknown> | null | undefined,
  parentInStock: boolean,
  selectedOptionCount: number,
  configurableAttributeCount: number
): boolean {
  if (isPackSizeOneSquareMeter(selectedProduct)) {
    return false;
  }

  if (selectedProduct && Object.keys(selectedProduct).length > 0) {
    return Boolean(selectedProduct['in_stock']);
  }

  if (configurableAttributeCount > 0 && selectedOptionCount === configurableAttributeCount) {
    return parentInStock;
  }

  return false;
}
